// URL and host logic shared by the browser apps and the Worker.
// No zod and no `process` here: the Worker imports this file directly.

/** Explicit per-site overrides, e.g. for preview deploys. */
export interface SiteOverrides {
	VITE_API_URL?: string | undefined;
	VITE_APP_URL?: string | undefined;
	VITE_MARKETING_URL?: string | undefined;
	VITE_STATUS_URL?: string | undefined;
	VITE_TURNSTILE_SITE_KEY?: string | undefined;
}

export type Site = "marketing" | "app" | "api" | "status";

/** Root domains InboxKit runs on. Subdomains map 1:1 to {@link Site}. */
export const PRODUCTION_ROOT = "inboxkit.dev";
export const LOCAL_ROOT = "inboxkit.local";

/** Cloudflare Turnstile test key: always passes, works on any hostname. */
export const TURNSTILE_TEST_SITE_KEY = "1x00000000000000000000AA";

const siteOverrides: Record<Site, keyof SiteOverrides> = {
	marketing: "VITE_MARKETING_URL",
	app: "VITE_APP_URL",
	api: "VITE_API_URL",
	status: "VITE_STATUS_URL",
};

const LOOPBACK_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]"]);

type Root = { protocol: string; domain: string };

function currentHref(): string | undefined {
	return (globalThis as { location?: { href?: string } }).location?.href;
}

function resolveRoot(from: string | URL | undefined): Root | undefined {
	if (!from) return undefined;
	const url = new URL(from);
	const host = url.hostname;
	for (const domain of [PRODUCTION_ROOT, LOCAL_ROOT]) {
		if (host === domain || host.endsWith(`.${domain}`)) {
			return { protocol: url.protocol, domain };
		}
	}
	if (LOOPBACK_HOSTS.has(host) || host.endsWith(".localhost")) {
		throw new Error(
			`Cannot derive InboxKit URLs from ${url.origin}. Open the site on *.${LOCAL_ROOT}, or set VITE_*_URL.`,
		);
	}
	return undefined;
}

export interface SiteUrlOptions {
	/** URL the code runs on. Defaults to `location.href` in the browser. */
	from?: string | URL;
	/** Parsed client env. Explicit `VITE_*_URL` values win over derivation. */
	env?: SiteOverrides;
}

/**
 * Origin of an InboxKit site, derived from the host the code runs on:
 * `app.inboxkit.local` → `https://status.inboxkit.local`,
 * `inboxkit.dev` → `https://api.inboxkit.dev`. Unknown hosts (previews)
 * fall back to production unless a `VITE_*_URL` override is set.
 */
export function siteUrl(site: Site, options: SiteUrlOptions = {}): string {
	const override = options.env?.[siteOverrides[site]];
	if (override) return override;
	const root = resolveRoot(options.from ?? currentHref()) ?? {
		protocol: "https:",
		domain: PRODUCTION_ROOT,
	};
	const host = site === "marketing" ? root.domain : `${site}.${root.domain}`;
	return `${root.protocol}//${host}`;
}

/** True when the code runs on a local development host. */
export function isLocalHost(from: string | URL | undefined = currentHref()) {
	if (!from) return false;
	const host = new URL(from).hostname;
	return (
		host === LOCAL_ROOT ||
		host.endsWith(`.${LOCAL_ROOT}`) ||
		LOOPBACK_HOSTS.has(host) ||
		host.endsWith(".localhost")
	);
}

/** Turnstile site key: the test key on local hosts, else the configured key. */
export function turnstileSiteKey(
	env: Pick<SiteOverrides, "VITE_TURNSTILE_SITE_KEY">,
	from: string | URL | undefined = currentHref(),
): string | undefined {
	return isLocalHost(from) ? TURNSTILE_TEST_SITE_KEY : env.VITE_TURNSTILE_SITE_KEY;
}

// ---------------------------------------------------------------------------
// URL validation, shared with the server parsers in index.ts. Kept here, and
// free of zod, so browser bundles do not ship a schema library to read a few
// optional build-time overrides.
// ---------------------------------------------------------------------------

export type ParsedUrl = { value: string; url: URL };

export function hasControlCharacters(value: string) {
	for (const character of value) {
		const codePoint = character.codePointAt(0);
		if (
			codePoint !== undefined &&
			(codePoint <= 0x1f || (codePoint >= 0x7f && codePoint <= 0x9f))
		) {
			return true;
		}
	}
	return false;
}

export function parseUrl(value: string): ParsedUrl | undefined {
	if (value !== value.trim() || hasControlCharacters(value)) {
		return undefined;
	}
	try {
		return { value, url: new URL(value) };
	} catch {
		return undefined;
	}
}

export function hasOriginOnlySuffix(value: string) {
	const trimmed = value.trim();
	const suffix = trimmed.match(/^https?:\/\/[^/?#]*(.*)$/i)?.[1];
	return suffix === "" || suffix === "/";
}

/** `value` as a bare http(s) origin (no path, credentials, query, or hash). */
export function toHttpOrigin(value: string): string | undefined {
	const parsed = parseUrl(value);
	if (!parsed || !["http:", "https:"].includes(parsed.url.protocol)) return undefined;
	const { url } = parsed;
	const bare =
		hasOriginOnlySuffix(value) &&
		!url.username &&
		!url.password &&
		url.pathname === "/" &&
		!url.search &&
		!url.hash;
	return bare ? url.origin : undefined;
}

const CLIENT_ORIGIN_KEYS = [
	"VITE_API_URL",
	"VITE_APP_URL",
	"VITE_MARKETING_URL",
	"VITE_STATUS_URL",
] as const;

/**
 * Browser configuration: optional per-site origin overrides and the Turnstile
 * key. Same rules and error as the server parsers; unknown keys are dropped.
 */
export function parseClientEnv(source: Record<string, unknown>): SiteOverrides {
	const env: SiteOverrides = {};
	const invalid: string[] = [];
	for (const key of CLIENT_ORIGIN_KEYS) {
		const value = source[key];
		if (value === undefined) continue;
		const origin = typeof value === "string" ? toHttpOrigin(value) : undefined;
		if (origin) env[key] = origin;
		else invalid.push(key);
	}
	const turnstile = source.VITE_TURNSTILE_SITE_KEY;
	if (typeof turnstile === "string") env.VITE_TURNSTILE_SITE_KEY = turnstile;
	else if (turnstile !== undefined) invalid.push("VITE_TURNSTILE_SITE_KEY");
	if (invalid.length > 0) {
		throw new Error(`Invalid client configuration: ${invalid.join(", ")}`);
	}
	return env;
}

export type ClientEnv = SiteOverrides;
